import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {SubmitForm} from '../../../sdk/interface/form/submitForm';
import {SyncValidators} from '../../../sdk/validator/SyncValidators';
import {AlertService} from '../../../sdk/service/alert.service';
import {ROUTES} from '../../../app.routes';
import {switchMap} from 'rxjs/operators';
import {
    NotificationRequest,
    NotificationResponse,
    Role,
    UserInvitation,
    WebUserInvitationService,
    WebUserService
} from 'web-angular-client';
import {Observable} from 'rxjs';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {AsyncValidators} from '../../../sdk/validator/AsyncValidators';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {Location} from '@angular/common';
import NotificationTypeEnum = NotificationRequest.NotificationTypeEnum;

@Component({
    selector: 'app-user-create',
    templateUrl: './user-invitation.component.html',
    styleUrls: ['./user-invitation.component.scss'],
    standalone: false
})
export class UserInvitationComponent extends SubmitForm implements OnInit
{
    selectedRole: Role;

    constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        private ngxLoader: NgxUiLoaderService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        protected webUserService: WebUserService,
        protected webUserInvitationService: WebUserInvitationService,
        private location: Location)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
    }

    get isValidatingUserEmail(): boolean
    {
        return this.form.controls['email'].pending;
    }

    ngOnInit()
    {
        this.form = this.formBuilder.group({
            email: [
                '',
                [Validators.required, Validators.email],
                AsyncValidators.validateUserForBusinessExists(this.webUserService)
            ],
            agentId: [
                '',
                [SyncValidators.validateAgentId()]
            ]
        });
    }

    /**
     * Sets the role selected using the role-selector component.
     */
    onRoleSelect(selectedRole: Role)
    {
        this.selectedRole = selectedRole;
    }

    submit()
    {
        const trimmedAgentId = this.f.agentId.value?.trim() || undefined;
        const invitation: UserInvitation = {
            email: this.f.email.value,
            agentId: trimmedAgentId,
            role: this.selectedRole
        };

        this.ngxLoader.start();
        this.loading = true;
        this.inviteUser(invitation).subscribe({
            next: (notificationResponse) =>
            {
                this.ngxLoader.stop();
                this.loading = false;
                this.alertService.success(notificationResponse.responseMessage, true);
                this.router.navigate([ROUTES.SECURE.USER.INVITATION.LIST.path]).then();
            },
            error: error =>
            {
                this.ngxLoader.stop();
                this.loading = false;
                this.alertService.error(error);
            }
        });
    }

    back()
    {
        this.location.back();
    }

    /**
     * Invoke the following chained API call:
     * 1. Create a user invitation.
     * 2. Send a user invitation notification.
     */
    protected inviteUser(userInvitation: UserInvitation): Observable<NotificationResponse>
    {
        return this.webUserInvitationService.createUserInvitation(
            userInvitation).pipe(switchMap(() =>
        {
            const notificationRequest: NotificationRequest = {
                email: userInvitation.email,
                notificationType: NotificationTypeEnum.EMAIL,
                initiatorType: 'MERCHANT'
            };
            return this.webUserInvitationService.sendUserInvitationNotification(notificationRequest);
        }));
    }
}